import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, Link2, Repeat, Tag, Edit, Trash2, Video } from 'lucide-react';
import type { EventInstance, RecurrenceEditScope, TimeFormat } from '../types';
import {
  formatTimeRange,
  getEventColor,
  buildRecurrenceLabel,
  parseRruleToRule,
  detectVideoLink,
  VIDEO_PROVIDER_LABELS,
} from '../utils';

interface EventDetailDialogProps {
  open: boolean;
  event: EventInstance | null;
  onClose: () => void;
  onEdit: (event: EventInstance) => void;
  onDelete: (event: EventInstance, scope: RecurrenceEditScope) => void;
  timeFormat?: TimeFormat;
}

export function EventDetailDialog({
  open,
  event,
  onClose,
  onEdit,
  onDelete,
  timeFormat = '12h',
}: EventDetailDialogProps) {
  if (!event) return null;

  const color = getEventColor(event);
  const rule = parseRruleToRule(event.rrule);
  const startDate = new Date(event.startAt);
  const videoCall = detectVideoLink(event);

  const handleDelete = (scope: RecurrenceEditScope) => {
    onDelete(event, scope);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className="w-4 h-4 rounded-full mt-1 shrink-0"
              style={{ backgroundColor: color }}
            />
            <div>
              <DialogTitle className="text-lg">{event.title}</DialogTitle>
              <DialogDescription className="mt-1">
                {formatTimeRange(event.startAt, event.endAt, event.isAllDay, timeFormat)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {startDate.toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>

          {/* Time */}
          {!event.isAllDay && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {formatTimeRange(event.startAt, event.endAt, false, timeFormat)}
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {event.location}
            </div>
          )}

          {/* Recurrence */}
          {event.isRecurring && rule && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Repeat className="h-4 w-4" />
              {buildRecurrenceLabel(rule)}
            </div>
          )}

          {/* Video call / URL */}
          {videoCall ? (
            <div className="flex items-center gap-2 text-sm">
              <Video className="h-4 w-4 text-muted-foreground" />
              <a
                href={videoCall.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
              >
                {VIDEO_PROVIDER_LABELS[videoCall.provider]}
              </a>
            </div>
          ) : event.url && /^https?:\/\//i.test(event.url) ? (
            <div className="flex items-center gap-2 text-sm">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline truncate"
              >
                {event.url}
              </a>
            </div>
          ) : null}

          {/* Tags */}
          {event.tags.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Tag className="h-4 w-4" />
              <div className="flex flex-wrap gap-1">
                {event.tags.map((tag) => (
                  <span key={tag} className="bg-muted px-2 py-0.5 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="text-sm mt-2 whitespace-pre-wrap text-muted-foreground border-t pt-3">
              {event.description}
            </div>
          )}

          {/* Calendar name */}
          {event.calendarName && (
            <div className="text-xs text-muted-foreground mt-2">Calendar: {event.calendarName}</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {event.isRecurring ? (
            <div className="flex gap-1 mr-auto">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete('this')}
                className="text-xs"
              >
                Delete This
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete('this_and_future')}
                className="text-xs"
              >
                This & Future
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete('all')}
                className="text-xs"
              >
                All Events
              </Button>
            </div>
          ) : (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDelete('all')}
              className="mr-auto"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onEdit(event);
              onClose();
            }}
          >
            <Edit className="h-3.5 w-3.5 mr-1" />
            Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
